import { parseDashboardSections } from "./parseDashboardSections";

/**
 * Dashboard sections as nested JSON (up to 4 name layers).
 *
 * Pure JSON shape — names you type are object keys; leaves are `{}`.
 * Example:
 * {
 *   "Overview": {},
 *   "Financials": { "Revenue": {}, "Opex": { "Payroll": {} } }
 * }
 */

export const MAX_SECTION_DEPTH = 4;

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const createSectionNode = (name = "", children = []) => ({
  id: newId(),
  name,
  children,
});

/** @param {unknown} raw */
const parseJsonMaybe = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      return JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  return null;
};

const isPlainObject = (value) =>
  value != null && typeof value === "object" && !Array.isArray(value);

/** Leaf node in JSON: empty object, empty array, null, or legacy "value" placeholder. */
const isEmptyLeafPayload = (value) => {
  if (value == null || value === "") return true;
  if (value === "value") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (isPlainObject(value) && Object.keys(value).length === 0) return true;
  return false;
};

/**
 * @param {unknown} value
 * @returns {import('./dashboardSectionsTree').SectionNode[]}
 */
const payloadValueToChildren = (value) => {
  if (isEmptyLeafPayload(value)) return [];

  if (Array.isArray(value)) {
    return value.flatMap((entry) => {
      if (!isPlainObject(entry)) return [];
      return Object.entries(entry).map(([name, childVal]) =>
        createSectionNode(name, payloadValueToChildren(childVal))
      );
    });
  }

  if (isPlainObject(value)) {
    return Object.entries(value).map(([name, childVal]) =>
      createSectionNode(name, payloadValueToChildren(childVal))
    );
  }

  return [];
};

/**
 * @param {Record<string, unknown>} obj
 */
const deserializePayload = (obj) => {
  if (!isPlainObject(obj)) return [];
  return Object.entries(obj).map(([name, value]) =>
    createSectionNode(name, payloadValueToChildren(value))
  );
};

/**
 * Legacy flat rows: "Section" or "Section,Sub,SubSub".
 * @param {string[]} flat
 */
export const sectionNodesFromLegacyFlat = (flat) => {
  const roots = [];
  const rootByName = new Map();

  flat.forEach((item) => {
    const value = String(item || "").trim();
    if (!value) return;
    const parts = value.split(",").map((p) => p.trim()).filter(Boolean);
    if (parts.length === 0) return;

    let currentNode = null;
    let currentChildren = null;

    parts.forEach((part, index) => {
      const depth = index + 1;
      if (depth > MAX_SECTION_DEPTH) return;

      if (depth === 1) {
        if (!rootByName.has(part.toLowerCase())) {
          const node = createSectionNode(part, []);
          rootByName.set(part.toLowerCase(), node);
          roots.push(node);
        }
        currentNode = rootByName.get(part.toLowerCase());
        currentChildren = currentNode.children;
        return;
      }

      if (!currentNode || !currentChildren) return;
      let child = currentChildren.find((c) => c.name.toLowerCase() === part.toLowerCase());
      if (!child) {
        child = createSectionNode(part, []);
        currentChildren.push(child);
      }
      currentNode = child;
      currentChildren = child.children;
    });
  });

  return roots;
};

/**
 * @param {unknown} raw - API `sections` field
 */
export const sectionNodesFromApi = (raw) => {
  const parsed = parseJsonMaybe(raw);
  if (isPlainObject(parsed)) {
    return deserializePayload(parsed);
  }

  if (Array.isArray(raw) && raw.length > 0) {
    const first = raw[0];
    if (isPlainObject(first)) {
      return deserializePayload(Object.fromEntries(raw.map((o, i) => [String(i), o])));
    }
    if (typeof first === "string") {
      const joined = raw.join("");
      const fromJoined = parseJsonMaybe(joined);
      if (isPlainObject(fromJoined)) {
        return deserializePayload(fromJoined);
      }
      const normalized = parseDashboardSections(raw);
      if (normalized.length > 0) {
        return sectionNodesFromLegacyFlat(normalized);
      }
      return sectionNodesFromLegacyFlat(raw.map(String));
    }
  }

  if (typeof raw === "string") {
    const fromString = parseJsonMaybe(raw);
    if (isPlainObject(fromString)) {
      return deserializePayload(fromString);
    }
    const normalized = parseDashboardSections(raw);
    if (normalized.length > 0) {
      return sectionNodesFromLegacyFlat(normalized);
    }
  }

  return [];
};

/**
 * Convert form tree → nested JSON for API `sections` (same shape as UI preview).
 * Unnamed nodes are skipped; their named descendants are merged into the parent object.
 * @param {Array<{ name?: string, children?: unknown[] }>} nodes
 */
const subtreeToSectionsPayload = (nodes) => {
  const result = {};
  (nodes || []).forEach((node) => {
    const name = String(node.name || "").trim();
    const childPayload = subtreeToSectionsPayload(node.children);

    if (name) {
      result[name] = Object.keys(childPayload).length === 0 ? {} : childPayload;
    } else {
      Object.assign(result, childPayload);
    }
  });
  return result;
};

/** Exact `sections` object sent on POST/PATCH — must match hierarchy preview JSON. */
export const buildDashboardSectionsRequestBody = (sectionItems) =>
  subtreeToSectionsPayload(sectionItems || []);

/** @deprecated alias — use buildDashboardSectionsRequestBody */
export const sectionNodesToPayload = buildDashboardSectionsRequestBody;

/**
 * @param {Array<{ name?: string, children?: unknown[] }>} nodes
 */
export const flattenSectionNodeNames = (nodes) => {
  const out = [];
  const walk = (list, prefix) => {
    (list || []).forEach((node) => {
      const name = String(node.name || "").trim();
      if (!name) return;
      const path = prefix ? `${prefix},${name}` : name;
      out.push(path);
      if (Array.isArray(node.children) && node.children.length > 0) {
        walk(node.children, path);
      }
    });
  };
  walk(nodes, "");
  return out;
};

/**
 * Wire format when API expects string[] (comma-separated paths).
 * @param {Array<{ name?: string, children?: unknown[] }>} sectionNodes
 * @returns {string[]}
 */
export const sectionNodesToApiSections = (sectionNodes) =>
  flattenSectionNodeNames(sectionNodes).filter(Boolean);

export const formatSectionBadge = (indexPath) => {
  const number = indexPath.map((i) => i + 1).join(".");
  if (indexPath.length === 1) return `Section ${number}`;
  return `Sub ${number}`;
};

export const flattenSectionsForTable = (nodes, depth = 1, prefix = "", indexPath = []) => {
  const rows = [];
  (nodes || []).forEach((node, index) => {
    const name = String(node.name || "").trim();
    if (!name) return;
    const currentPath = [...indexPath, index];
    const path = prefix ? `${prefix} > ${name}` : name;
    rows.push({
      key: path,
      name,
      depth,
      path,
      badge: formatSectionBadge(currentPath),
    });
    if (Array.isArray(node.children) && node.children.length > 0) {
      rows.push(...flattenSectionsForTable(node.children, depth + 1, path, currentPath));
    }
  });
  return rows;
};

const walkSectionLadderLines = (nodes, prefixes, indexPath, includeBadges) => {
  const lines = [];
  (nodes || []).forEach((node, index) => {
    const name = String(node.name || "").trim();
    if (!name) {
      if (Array.isArray(node.children) && node.children.length > 0) {
        lines.push(...walkSectionLadderLines(node.children, prefixes, indexPath, includeBadges));
      }
      return;
    }

    const isLast = index === (nodes || []).length - 1;
    const branch = isLast ? "└─" : "├─";
    const indent = prefixes.map((continues) => (continues ? "│  " : "   ")).join("");
    const currentPath = [...indexPath, index];
    const label = includeBadges ? `${formatSectionBadge(currentPath)}: ${name}` : name;
    lines.push(`${indent}${branch} ${label}`);
    if (Array.isArray(node.children) && node.children.length > 0) {
      lines.push(
        ...walkSectionLadderLines(node.children, [...prefixes, !isLast], currentPath, includeBadges)
      );
    }
  });
  return lines;
};

/** @param {Array<{ name?: string, children?: unknown[] }>} nodes */
export const buildSectionLadderLines = (nodes, includeBadges = true) =>
  walkSectionLadderLines(nodes, [], [], includeBadges);

export const formatSectionsPayloadPreview = (payload) => {
  if (payload == null) return "{}";
  if (typeof payload === "object" && !Array.isArray(payload) && Object.keys(payload).length === 0) {
    return "{}";
  }
  return JSON.stringify(payload, null, 2);
};

/** Pretty JSON string for the form preview — identical to request `sections` body. */
export const formatDashboardSectionsPayloadPreview = (sectionItems) =>
  formatSectionsPayloadPreview(buildDashboardSectionsRequestBody(sectionItems));
